import { useCallback, useMemo, useState } from 'react';

import { Box, SimpleGrid } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import {
  Button,
  DECIMAL_PROPS,
  MonthPicker,
  NumberInput,
  Select,
  YesNoRadioButton,
} from 'components';
import {
  CreditOptionsResponse,
  CreditProfessionalSituation,
  CreditResponse,
  EmploymentType,
  TravelingType,
} from 'types';
import { AppUtils } from 'utils';

interface CreditRequestEmploymentProps {
  credit: CreditResponse;
  form: any;
  options: CreditOptionsResponse;
  partner: boolean;
  readOnly?: boolean;
}

interface EmploymentObject {
  creditRequestId?: number;
  professionalSituation?: CreditProfessionalSituation;
  salary?: number;
  thirteenthSalary?: boolean;
  bonusReceived?: boolean;
  socialHelp?: boolean;
  employmentType?: EmploymentType;
  contractStartDate?: Date;
  contractEndDate?: Date;
  travelingToWork?: TravelingType;
  partner?: boolean;
}
const tempEmploymentObject = {
  creditRequestId: undefined,
  professionalSituation: undefined,
  salary: undefined,
  thirteenthSalary: undefined,
  bonusReceived: undefined,
  socialHelp: undefined,
  employmentType: undefined,
  contractStartDate: undefined,
  contractEndDate: undefined,
  travelingToWork: undefined,
  partner: false,
} as EmploymentObject;

const propertyName = 'employments';

const CreditRequestEmployment = ({
  credit,
  form,
  options,
  partner,
  readOnly = false,
}: CreditRequestEmploymentProps) => {
  const { t } = useTranslation();
  const [visibleFields, setVisibleFields] = useState<string[]>(
    AppUtils.getVisibleEmploymentFields(credit[propertyName]),
  );

  const getInitialData = useCallback(() => {
    const formValues: EmploymentObject[] | undefined = credit[propertyName];
    let array = [];
    if (!formValues || formValues.length === 0) {
      array.push(tempEmploymentObject);
    } else {
      const filtered = formValues.filter((el) => el.partner === partner);
      if (filtered.length > 0) {
        array = filtered.map((el) => ({
          ...el,
          contractStartDate: el.contractStartDate ? new Date(el.contractStartDate) : undefined,
          contractEndDate: el.contractEndDate ? new Date(el.contractEndDate) : undefined,
        }));
      } else {
        array.push(tempEmploymentObject);
      }
    }
    return array;
  }, []);

  const employmentForm = useForm({
    mode: 'uncontrolled',
    initialValues: getInitialData() as EmploymentObject[],
    onValuesChange(values, previous) {
      if (values[values.length - 1] !== tempEmploymentObject) {
        updateCredit(values, previous);
      }
    },
  });

  const updateCredit = useCallback(async (values: any, previous: any) => {
    const prevVisibleFields = AppUtils.getVisibleEmploymentFields(previous);
    const newVisibleFields = AppUtils.getVisibleEmploymentFields(values);
    AppUtils.clearArrayFieldsWhenUpdating(values, previous, newVisibleFields);
    AppUtils.updateArrayFieldsWhenVisibilityChanges(
      prevVisibleFields,
      newVisibleFields,
      values,
      employmentForm,
    );

    const formValues: EmploymentObject[] | undefined = form.getValues()[propertyName];
    const tempArray: EmploymentObject[] = formValues
      ? formValues.filter((el: EmploymentObject) => el.partner !== partner)
      : [];
    Object.keys(values).forEach((key) => {
      const item: EmploymentObject = { ...values[key] };
      const isNotEmpty =
        item.professionalSituation ||
        item.salary ||
        item.thirteenthSalary ||
        item.bonusReceived ||
        item.socialHelp ||
        item.employmentType ||
        item.contractStartDate ||
        item.contractEndDate ||
        item.travelingToWork;
      if (isNotEmpty) {
        item.creditRequestId = credit.id;
        item.partner = partner;
        tempArray.push(item);
      }
    });
    form.setFieldValue(propertyName, tempArray);
    setVisibleFields(newVisibleFields);
  }, []);

  const transformedKeys = useMemo(() => {
    const form = employmentForm.getValues();
    return Object.keys(form);
  }, [employmentForm]);

  return (
    <Box p={'md'} pt={'xs'} mt={'sm'}>
      {Object.keys(transformedKeys).map((key: any, index: number) => (
        <Box
          key={key}
          style={
            index > 0 ? { borderTop: '1px solid var(--mantine-color-gray-4)', marginTop: 16 } : {}
          }
        >
          <SimpleGrid cols={4} mt={'sm'}>
            <Select
              label={t(`credit.employment.professionalSituation.label`)}
              data={options['professionalSituation'].map((el) => ({
                value: el,
                label: t(`credit.employment.professionalSituation.options.${el}`),
              }))}
              key={employmentForm.key(`${index}.professionalSituation`)}
              {...employmentForm.getInputProps(`${index}.professionalSituation`)}
              clearable={true}
              required={true}
              disabled={readOnly}
            />
            <NumberInput
              label={t(`credit.employment.salary.label`)}
              key={employmentForm.key(`${index}.salary`)}
              {...employmentForm.getInputProps(`${index}.salary`)}
              min={0}
              required={true}
              {...DECIMAL_PROPS}
              disabled={readOnly}
            />
            {visibleFields.includes(`employments.${index}.requesterPaysSalary`) && (
              <YesNoRadioButton
                label={t(`credit.employment.requesterPaysSalary.label`)}
                name={`${index}.requesterPaysSalary`}
                form={employmentForm}
                t={t}
                disabled={readOnly}
              />
            )}
            {transformedKeys.length > 1 && !readOnly && (
              <Box style={{ textAlign: 'right' }}>
                <Button
                  variant={'transparent'}
                  size={'compact-sm'}
                  color={'var(--mantine-color-error)'}
                  mb={6}
                  onClick={() => AppUtils.removeObject(employmentForm, index, tempEmploymentObject)}
                >
                  <IconTrash size={20} />
                </Button>
              </Box>
            )}
            {visibleFields.includes(`employments.${index}.thirteenthSalary`) && (
              <YesNoRadioButton
                label={t(`credit.employment.thirteenthSalary.label`)}
                name={`${index}.thirteenthSalary`}
                form={employmentForm}
                t={t}
                disabled={readOnly}
              />
            )}
            {visibleFields.includes(`employments.${index}.bonusReceived`) && (
              <YesNoRadioButton
                label={t(`credit.employment.bonusReceived.label`)}
                name={`${index}.bonusReceived`}
                form={employmentForm}
                t={t}
                disabled={readOnly}
              />
            )}
            {visibleFields.includes(`employments.${index}.thirteenthPension`) && (
              <YesNoRadioButton
                label={t(`credit.employment.thirteenthPension.label`)}
                name={`${index}.thirteenthPension`}
                form={employmentForm}
                t={t}
                disabled={readOnly}
              />
            )}
            {visibleFields.includes(`employments.${index}.socialHelp`) && (
              <YesNoRadioButton
                label={t(`credit.employment.socialHelp.label`)}
                name={`${index}.socialHelp`}
                form={employmentForm}
                t={t}
                disabled={readOnly}
              />
            )}
            {visibleFields.includes(`employments.${index}.employmentType`) && (
              <Select
                label={t(`credit.employment.employmentType.label`)}
                data={options['employmentType'].map((el) => ({
                  value: el,
                  label: t(`credit.employment.employmentType.options.${el}`),
                }))}
                key={employmentForm.key(`${index}.employmentType`)}
                {...employmentForm.getInputProps(`${index}.employmentType`)}
                clearable={true}
                disabled={readOnly}
              />
            )}
            {visibleFields.includes(`employments.${index}.contractStartDate`) && (
              <MonthPicker
                label={t(`credit.employment.contractStartDate.label`)}
                key={employmentForm.key(`${index}.contractStartDate`)}
                {...employmentForm.getInputProps(`${index}.contractStartDate`)}
                minDate={
                  visibleFields.includes(`employments.${index}.contractEndDate`)
                    ? moment().subtract(2, 'years')
                    : undefined
                }
                maxDate={new Date()}
                disabled={readOnly}
              />
            )}
            {visibleFields.includes(`employments.${index}.contractEndDate`) && (
              <MonthPicker
                label={t(`credit.employment.contractEndDate.label`)}
                key={employmentForm.key(`${index}.contractEndDate`)}
                {...employmentForm.getInputProps(`${index}.contractEndDate`)}
                minDate={new Date()}
                maxDate={moment().add(6, 'months')}
                disabled={readOnly}
              />
            )}
            {visibleFields.includes(`employments.${index}.travelingToWork`) && (
              <Select
                label={t(`credit.employment.travelingToWork.label`)}
                data={options['travelingToWork'].map((el) => ({
                  value: el,
                  label: t(`credit.employment.travelingToWork.options.${el}`),
                }))}
                key={employmentForm.key(`${index}.travelingToWork`)}
                {...employmentForm.getInputProps(`${index}.travelingToWork`)}
                clearable={true}
                disabled={readOnly}
              />
            )}
          </SimpleGrid>
        </Box>
      ))}
      {!readOnly && (
        <Button
          onClick={() => AppUtils.addNewObject(employmentForm, tempEmploymentObject)}
          size={'compact-xs'}
          variant={'transparent'}
          mt={'md'}
        >
          <>
            <Box mr={4}>
              <IconPlus size={16} />
            </Box>
            {t('credit.employment.add')}
          </>
        </Button>
      )}
    </Box>
  );
};

export default CreditRequestEmployment;
